import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Container, Card, Row, Col, Form } from "react-bootstrap";
import { MdEdit, MdDelete } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

const PayrollSchedule = () => {
    const [payrollData, setPayrollData] = useState([]);
    const [form, setForm] = useState({
        payroll_schedule_id: "",
        start_date: "",
        end_date: "",
        processing_date: "",
        payroll_month: ""
    });
    const [editId, setEditId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [monthList, setMonthList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const navigate = useNavigate();
    const location = useLocation();
    const userObj = JSON.parse(sessionStorage.getItem('user')) || {};
    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "Principal"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

    const fetchData = async () => {
  setIsLoading(true);
  try {
    const response = await axios.post(baseUrl + "/payrollschedule/read", {
      action: "READ",
      month_id: null,
      school_id: userObj.school_id,
    });

    
    setPayrollData(Array.isArray(response.data) ? response.data : []);
    setFilteredRecords(Array.isArray(response.data) ? response.data : []);
  } catch (err) {
    const status = err?.response?.status;
    const msg = err?.response?.data?.message;


    if (status === 404 || msg === "No payroll schedule found.") {
      setPayrollData([]);
      setFilteredRecords([]);
    //   toast.warning("No payroll schedule found.");
    } else {
      toast.error("Error loading payroll schedules.");
    }
  } finally {
    setIsLoading(false);
  }
};


    const fetchMonths = async () => {
        try {
            const response = await axios.post(baseUrl + '/months/', { action: 'READ' });
            setMonthList(response.data);
        } catch (error) {
            toast.error("Failed to load months.");
        }
    };

    useEffect(() => {
        fetchMonths();
        fetchData();
    }, []);

    const handleEditClick = (payroll_schedule_id) => {
        const scheduleToEdit = payrollData.find(item => item.payroll_schedule_id === payroll_schedule_id);
        if (scheduleToEdit) {
            setForm({
                payroll_schedule_id: scheduleToEdit.payroll_schedule_id,
                start_date: scheduleToEdit.start_date,
                end_date: scheduleToEdit.end_date,
                processing_date: scheduleToEdit.processing_date,
                payroll_month: scheduleToEdit.month_id,
            });
            setEditId(scheduleToEdit.payroll_schedule_id);
        }
    };

    const handleDeleteClick = async (payroll_schedule_id) => {
        const confirmDelete = window.confirm("Are you sure you want to change the status?");
        if (!confirmDelete) return;

        try {
            await axios.post(baseUrl + '/payrollschedule/', {
                action: 'DELETE',
                payroll_schedule_id: payroll_schedule_id,
                school_id: userObj.school_id
            });
            toast.success("Record Set to InActive");
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete payroll schedule.");
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm(prev => ({ ...prev, [id]: value }));
    };

    const handleSubmitPayrollSchedule = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                action: editId ? 'UPDATE' : 'CREATE',
                ...(editId && { payroll_schedule_id: form.payroll_schedule_id }),
                start_date: form.start_date,
                end_date: form.end_date,
                processing_date: form.processing_date,
                month_id: form.payroll_month,
                school_id: userObj.school_id,
            };

            await axios.post(baseUrl + '/payrollschedule/', payload);

            toast.success(
                editId
                    ? "Payroll schedule updated successfully"
                    : "Payroll schedule added successfully"
            );

            handleResetForm();
            fetchData();

        } catch (err) {
            console.error(err);

            const errorMsg =
                (err.response && err.response.data && err.response.data.error) ||
                (err.message ? err.message : "");

            if (errorMsg.includes("Payroll schedule for this month already exists")) {
                toast.error("Payroll schedule for this month already exists.");
            } else {
                toast.error("Failed to submit payroll schedule.");
            }
        }
    };

    const handleResetForm = () => {
        setForm({
            payroll_schedule_id: "",
            start_date: "",
            end_date: "",
            processing_date: "",
            payroll_month: ""
        });
        setEditId(null);
        setFilteredRecords(payrollData);
    };

    const handleSearchChange = (e) => {
        const query = e.target.value.toLowerCase().trim();
        setSearchQuery(query);
        const filtered = payrollData.filter(item =>
            ["start_date", "end_date", "processing_date", "month_name"]
                .some(key => String(item[key] || "").toLowerCase().includes(query))
        );
        setFilteredRecords(filtered);
    };

    const columns = [
        {
            field: "start_date",
            headerName: "Start Date",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "end_date",
            headerName: "End Date",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "processing_date",
            headerName: "Processing Date",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "month_name",
            headerName: "Payroll Month",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || '-'}>
                    <span className="cell-wrap">{params.value || '-'}</span>
                </Tooltip>
            ),
        },
        ...(canSubmit
            ? [
                {
                    field: "actions",
                    headerName: "Actions",
                    flex: 1,
                    sortable: false,
                    renderCell: (params) =>
                        params.row.processing_date !== "No records found" ? (
                            <div className="tableActions">
                                <Tooltip title="Edit" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() =>
                                            handleEditClick(params.row.payroll_schedule_id)
                                        }
                                    >
                                        <MdEdit />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() =>
                                            handleDeleteClick(params.row.payroll_schedule_id)
                                        }
                                    >
                                        <MdDelete />
                                    </span>
                                </Tooltip>
                            </div>
                        ) : null,
                },
            ]
            : []),
    ];

    return (
        <div className="pageBody">
            <ToastContainer />
            <h6 className="commonTableTitle">Payroll Schedule</h6>

            {/* Add/Edit Payroll Schedule Form Section */}
            <section id="payrollFormSection">
                <Container fluid>
                    <Card>
                        <Card.Body>
                            <form onSubmit={handleSubmitPayrollSchedule}>
                                <Row>
                                    <Col xs={12}>
                                        <h6 className="commonSectionTitle">
                                            {editId !== null ? "Edit Payroll Schedule" : "Add Payroll Schedule"}
                                        </h6>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>Start Date <span className="requiredStar">*</span></Form.Label>
                                                <Form.Control
                                                    required
                                                    type="date"
                                                    id="start_date"
                                                    value={form.start_date}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>End Date <span className="requiredStar">*</span></Form.Label>
                                                <Form.Control
                                                    required
                                                    type="date"
                                                    id="end_date"
                                                    value={form.end_date}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>Processing Date <span className="requiredStar">*</span></Form.Label>
                                                <Form.Control
                                                    required
                                                    type="date"
                                                    id="processing_date"
                                                    value={form.processing_date}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className='commonInput'>
                                            <Form.Group>
                                                <Form.Label>Payroll Month <span className='requiredStar'>*</span></Form.Label>
                                                <Form.Select
                                                    id="payroll_month"
                                                    value={form.payroll_month}
                                                    required
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="" disabled hidden>Select Month</option>
                                                    {monthList.map((month) => (
                                                        <option key={month.month_id} value={month.month_id}>
                                                            {month.month_name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-between mt-3">
                                    <Button
                                        type="button"
                                        variant="primary"
                                        className="btn-info clearBtn"
                                        onClick={handleResetForm}
                                    >
                                        Reset
                                    </Button>
                                    {canSubmit && (
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="btn-success primaryBtn"
                                        >
                                            {editId !== null ? "Update" : "Add"}
                                        </Button>
                                    )}
                                </div>
                            </form>
                        </Card.Body>
                    </Card>
                </Container>
            </section>

            {/* Payroll Schedule Table Section */}
            <section className="mt-4">
                <div className="commonDataTableHead">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        </div>

                        <div>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                className="searchInput"
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>
                </div>

                <div
                    className="commonTable height100"
                    style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
                >
                    <div className="tableBody">
                        {isLoading ? (
                            <div className="loadingContainer">
                                <img src={loading} alt="Loading..." className="loadingGif" />
                            </div>
                        ) : (
                            <AppDataGrid
                                rows={Array.isArray(filteredRecords) ? filteredRecords : []}  
                                columns={columns}
                                getRowId={(row) => row.payroll_schedule_id}
                                showToolbar={false}
                                pagination
                                pageSizeOptions={[10, 20, 50]}
                                density="compact"
                                maxHeight={550}
                            />
                        )}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default PayrollSchedule;
